import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StoreFormModal = ({ isOpen, onClose, onSubmit, store = null, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    address: '',
    size: '',
    monthlyRent: '',
    contactPerson: '',
    phone: '',
    email: '',
    layoutFile: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const locationOptions = [
    { value: 'dhaka', label: 'Dhaka' },
    { value: 'chittagong', label: 'Chittagong' },
    { value: 'sylhet', label: 'Sylhet' },
    { value: 'rajshahi', label: 'Rajshahi' },
    { value: 'khulna', label: 'Khulna' },
    { value: 'barisal', label: 'Barisal' },
    { value: 'rangpur', label: 'Rangpur' },
    { value: 'mymensingh', label: 'Mymensingh' }
  ];

  useEffect(() => {
    if (store && mode === 'edit') {
      setFormData({
        name: store?.name || '',
        location: store?.location?.toLowerCase() || '',
        address: store?.address || '',
        size: store?.size?.toString() || '',
        monthlyRent: store?.monthlyRent?.toString() || '',
        contactPerson: store?.contactPerson || '',
        phone: store?.phone || '',
        email: store?.email || '',
        layoutFile: null
      });
    } else {
      setFormData({
        name: '',
        location: '',
        address: '',
        size: '',
        monthlyRent: '',
        contactPerson: '',
        phone: '',
        email: '',
        layoutFile: null
      });
    }
    setErrors({});
  }, [store, mode, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes?.includes(file?.type)) {
        setErrors(prev => ({ ...prev, layoutFile: 'Please upload a valid file (JPG, PNG, JPEG, PDF)' }));
        return;
      }
      // Validate file size (max 10MB)
      if (file?.size > 10 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, layoutFile: 'File size must be less than 10MB' }));
        return;
      }
      handleInputChange('layoutFile', file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Store name is required';
    }

    if (!formData?.location) {
      newErrors.location = 'Location is required';
    }

    if (!formData?.address?.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData?.size || isNaN(formData?.size) || parseFloat(formData?.size) <= 0) {
      newErrors.size = 'Valid store size is required';
    }

    if (!formData?.monthlyRent || isNaN(formData?.monthlyRent) || parseFloat(formData?.monthlyRent) <= 0) {
      newErrors.monthlyRent = 'Valid monthly rent is required';
    }

    if (!formData?.contactPerson?.trim()) {
      newErrors.contactPerson = 'Contact person is required';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^(\+88)?01[3-9]\d{8}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = 'Valid Bangladeshi phone number is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Valid email address is required';
    }

    // Layout file validation for create mode
    if (mode === 'create' && !formData?.layoutFile) {
      newErrors.layoutFile = 'Store layout file is required for new stores';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const size = parseFloat(formData?.size);
      const monthlyRent = parseFloat(formData?.monthlyRent);
      const rentPerSqFt = monthlyRent / size;

      const storeData = {
        ...formData,
        size,
        monthlyRent,
        rentPerSqFt,
        location: locationOptions?.find(opt => opt?.value === formData?.location)?.label || formData?.location,
        hasCurrentLayout: mode === 'create' ? Boolean(formData?.layoutFile) : store?.hasCurrentLayout
      };

      await onSubmit(storeData);
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '৳0';
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    })?.format(amount)?.replace('BDT', '৳');
  };

  const calculatedRentPerSqFt = formData?.size && formData?.monthlyRent 
    ? parseFloat(formData?.monthlyRent) / parseFloat(formData?.size)
    : 0;

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'FileText';
      case 'jpg': case'jpeg': case'png': return 'Image';
      default: return 'File';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Store" size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {mode === 'edit' ? 'Edit Store' : 'Add New Store'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {mode === 'edit' ?'Update store information and settings' :'Enter store details and upload layout to create store'
                  }
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Store Name"
                type="text"
                placeholder="Enter store name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />
              <Select
                label="Location"
                placeholder="Select location"
                options={locationOptions}
                value={formData?.location}
                onChange={(value) => handleInputChange('location', value)}
                error={errors?.location}
                required
              />
            </div>
            <Input
              label="Address"
              type="text"
              placeholder="Enter complete address"
              value={formData?.address}
              onChange={(e) => handleInputChange('address', e?.target?.value)}
              error={errors?.address}
              required
              className="mt-4"
            />
          </div>

          {/* Store Dimensions & Rent */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Store Dimensions & Rent</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Store Size (sq ft)"
                type="number"
                placeholder="Enter size in square feet"
                value={formData?.size}
                onChange={(e) => handleInputChange('size', e?.target?.value)}
                error={errors?.size}
                required
                min="1"
              />
              <Input
                label="Monthly Rent (৳)"
                type="number"
                placeholder="Enter monthly rent amount"
                value={formData?.monthlyRent}
                onChange={(e) => handleInputChange('monthlyRent', e?.target?.value)}
                error={errors?.monthlyRent}
                required
                min="1"
              />
            </div>
            
            {/* Calculated Rent per Sq Ft */}
            {calculatedRentPerSqFt > 0 && (
              <div className="mt-4 p-4 bg-accent/10 border border-accent/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <Icon name="Calculator" size={16} className="text-accent" />
                  <span className="text-sm font-medium text-accent">Calculated Rent per Sq Ft:</span>
                  <span className="text-lg font-bold text-accent">
                    {formatCurrency(calculatedRentPerSqFt)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Layout Section */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">
              Store Layout {mode === 'create' && <span className="text-error">*</span>}
            </h3>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {mode === 'create' ?'Store Layout File (Required)' :'Upload New Layout (Optional)'
                }
              </label>
              <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
                errors?.layoutFile ? 'border-error' : 'border-border'
              }`}>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="store-layout-upload"
                />
                <label 
                  htmlFor="store-layout-upload"
                  className="cursor-pointer block"
                >
                  {formData?.layoutFile ? (
                    <div className="flex items-center gap-2 justify-center">
                      <Icon name={getFileIcon(formData?.layoutFile?.name)} size={20} className="text-primary" />
                      <span className="text-sm text-foreground">{formData?.layoutFile?.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({(formData?.layoutFile?.size / 1024 / 1024)?.toFixed(2)} MB)
                      </span>
                    </div>
                  ) : (
                    <div>
                      <Icon name="Upload" size={24} className="text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        JPG, PNG, JPEG, PDF (Max 10MB)
                      </p>
                    </div>
                  )}
                </label>
              </div>
              {errors?.layoutFile && (
                <p className="text-sm text-error mt-1">{errors?.layoutFile}</p>
              )}
              {mode === 'create' && (
                <p className="text-xs text-muted-foreground mt-2">
                  A layout file is required to create a new store. This will be set as the initial layout version.
                </p>
              )}
              {mode === 'edit' && (
                <p className="text-xs text-muted-foreground mt-2">
                  Upload a new layout file to replace the current layout. Previous version will be kept in history.
                </p>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Contact Person"
                type="text"
                placeholder="Enter contact person name"
                value={formData?.contactPerson}
                onChange={(e) => handleInputChange('contactPerson', e?.target?.value)}
                error={errors?.contactPerson}
                required
              />
              <Input
                label="Phone Number"
                type="tel"
                placeholder="01XXXXXXXXX"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                error={errors?.phone}
                required
              />
            </div>
            <Input
              label="Email Address"
              type="email"
              placeholder="Enter email address"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
              className="mt-4"
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              iconName={mode === 'edit' ? 'Save' : 'Plus'}
              iconPosition="left"
            >
              {mode === 'edit' ? 'Update Store' : 'Add Store'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StoreFormModal;